import { llmGatewayApi } from "@app/api";
import type {
  DeployLlmGatewayDataRetentionMode,
  DeployLlmGatewayModel,
  DeployLlmGatewayModelResponse,
} from "@app/types";

type LlmGatewayModelsResponse = {
  data: DeployLlmGatewayModelResponse[];
};

/** The catalog models an organization's keys may call, given its allowlist. */
export const allowedLlmGatewayModels = (
  allowlist: string[],
  catalog: DeployLlmGatewayModel[],
) => {
  const isRestricted = allowlist.length > 0 && !allowlist.includes("*");
  return isRestricted
    ? catalog.filter((model) => allowlist.includes(model.id))
    : catalog;
};

const perMillionTokens = (price: string) =>
  Number.parseFloat(price) * 1_000_000;

const dataRetentionModes: DeployLlmGatewayDataRetentionMode[] = [
  "zdr",
  "private",
];

/**
 * Narrows the gateway's retention mode to one this UI can label. Anything
 * else, absent or a mode added later, is unknown rather than mislabelled.
 */
const deserializeDataRetentionMode = (
  mode: string | undefined,
): DeployLlmGatewayDataRetentionMode | undefined =>
  dataRetentionModes.find((known) => known === mode);

export const deserializeLlmGatewayModel = (
  payload: DeployLlmGatewayModelResponse,
): DeployLlmGatewayModel => ({
  id: payload.id,
  name: payload.normalized_name || payload.id,
  contextLength: payload.context_length,
  maxInputTokens: payload.max_input_tokens,
  maxOutputTokens: payload.max_output_tokens || 0,
  inputCostPerMillionTokens: perMillionTokens(payload.pricing.prompt),
  outputCostPerMillionTokens: perMillionTokens(payload.pricing.completion),
  dataRetentionMode: deserializeDataRetentionMode(payload.data_retention_mode),
});

export const fetchLlmGatewayModels = llmGatewayApi.get<
  never,
  LlmGatewayModelsResponse
>(["/metadata/models", "v2"], function* (ctx, next) {
  (ctx as any).noToken = true;

  yield* next();

  if (!ctx.json.ok) {
    return;
  }

  const models = ctx.json.value.data.map(deserializeLlmGatewayModel);
  ctx.loader = { meta: { models } };
});
